import { Component, ErrorInfo, PropsWithChildren, ReactNode, useCallback, useState } from 'react';
import { untilIdle } from '../util/untilIdle';

/** Manages state about errors in a React tree.
 *
 * Returns a tuple of `[wrap, caught, clear]`, where:
 *
 * - `wrap` wraps children with an error handler
 * - `caught` contains information about an error caught from the tree
 * - `clear` clears the caught error
 *
 * After catching an error, the wrapped children will be unmounted until the error is cleared.
 */
export const useErrorBoundary = () => {
  const [caught, setCaught] = useState<ReactTreeError | null>(null);

  const clear = useCallback(async () => {
    /** We wait until the JS process is idle before clearing the error flag,
     * to avoid getting caught in an endless error loop
     */
    await untilIdle();
    return setCaught(null);
  }, []);

  const wrap = useCallback(
    (children?: ReactNode) =>
      caught ? null : <RawErrorBoundary onError={setCaught}>{children}</RawErrorBoundary>,
    [caught],
  );

  return [wrap, caught, clear] as const;
};

interface ReactTreeError {
  error: Error;
  info: ErrorInfo;
}

class RawErrorBoundary extends Component<
  PropsWithChildren<{ onError: (error: ReactTreeError) => void }>,
  { caught: boolean }
> {
  state = { caught: false };
  static getDerivedStateFromError = () => ({ caught: true });

  componentDidCatch: Component['componentDidCatch'] = (error, info) => {
    this.props.onError({ error, info });
  };

  render = () => <>{!this.state.caught && this.props.children}</>;
}
